import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useRouterState, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AppLayout, PendingPublicActionResumeHost, SharePopover, setSmallScreenGateExempt, titleCaseSlug, useCampaignShare, useClarityIdentity, type AppSidebarProps, type CampaignSearch, type NavItem } from '@discovr/ui';
import { useAuth, useProfile, ApiError } from '@discovr/common';
import { campaignListOptions } from '@discovr/features';
import { brandApi } from '@/lib/api';
import { House, PaperPlaneTilt, Plug, Gear, Question, Plus } from '@discovr/ui/design-system/icons';
import discovrLogo from '@discovr/ui/assets/logo-wordmark.svg';
import discovrMark from '@discovr/ui/assets/logo-icon-black.svg';
import { isShopifyClaimLandingUrl } from '@/lib/shopifyClaimLanding';
import { useBrandSidebarUser } from '@/lib/useBrandSidebarUser';
import { hardNavigate, SIGNED_OUT_PATH } from '@/auth/hardNavigate';
import { BrandBreadcrumb } from './BrandBreadcrumb';
import { DraftStatusBadge } from '@/dashboard/NewCampaign/DraftStatusBadge';

// Full page loads, not router navigations: the Clarity tag has no stop, and a
// reload is what ends the recording and the identified session (issue #727).
const SESSION_EXPIRED_PATH = '/login?expired=true';

function isCampaignDetailRoute(pathname: string): boolean {
  return pathname.startsWith('/campaign/');
}

export default function BrandLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  useState(() => {
    if (isShopifyClaimLandingUrl()) setSmallScreenGateExempt(true);
  });
  useEffect(() => {
    if (pathname !== '/integrations') setSmallScreenGateExempt(false);
  }, [pathname]);
  // Share-link (brand → authenticated brand-app deep link). useCampaignShare is
  // enabled only once a campaign-detail page has seeded the stores; canShare is
  // false everywhere else, so the header share icon only appears on a campaign.
  const { canShare, makeUrl } = useCampaignShare();
  const shareSearch = useSearch({ strict: false }) as Partial<CampaignSearch>;
  const { user, loading, signOut } = useAuth();
  const { data: profile, isError: profileIsError, error: profileError } = useProfile();

  const { userInitials, userName, userRole } = useBrandSidebarUser();

  // Microsoft Clarity (session recordings + heatmaps), started only inside the
  // signed-in layout. VITE_CLARITY_PROJECT_ID is injected solely in the prod
  // deploy job, so Clarity is production-only.
  useClarityIdentity(import.meta.env.VITE_CLARITY_PROJECT_ID);

  // The entry guard lives in the route's beforeLoad; this effect only handles a
  // 401 surfacing during an active session. Only genuine 401s sign out —
  // network errors and 5xx don't invalidate the session.
  useEffect(() => {
    if (!loading && user && profileIsError) {
      const isAuthFailure = profileError instanceof ApiError && profileError.httpStatus === 401;
      if (isAuthFailure) {
        void signOut().finally(() => hardNavigate(SESSION_EXPIRED_PATH));
      }
    }
  }, [loading, user, profileIsError, profileError, signOut]);

  useQuery({ ...campaignListOptions(1), enabled: !!user });
  useQuery({
    queryKey: ['brand-dashboard-campaigns'],
    queryFn: () => brandApi.getDashboardCampaigns(5),
    staleTime: 30_000,
    enabled: !!user,
  });

  // Primary nav (Figma "Home / Campaign / Integration"). Integration routes to
  // /integrations — always visible; cards render in a connected-looking state
  // regardless of backend config, and reveal "not ready yet" only on Connect.
  const navItems: NavItem[] = [
    { id: 'overview', href: '/', label: 'Home', icon: House },
    {
      id: 'campaigns',
      href: '/campaigns',
      label: 'Campaign',
      icon: PaperPlaneTilt,
      // Campaign detail lives on the singular `/campaign/<slug>` path, which
      // doesn't share the `/campaigns` prefix — list it so the section stays
      // highlighted while a campaign is open.
      activePaths: ['/campaigns', '/campaign/'],
    },
    { id: 'integration', href: '/integrations', label: 'Integration', icon: Plug },
  ];

  // Footer nav (Figma "Settings / Help Centre").
  const footerItems: NavItem[] = [
    { id: 'settings', href: '/settings', label: 'Settings', icon: Gear },
    { id: 'help', href: '/help', label: 'Help Centre', icon: Question },
  ];

  // Active section = the item whose href (or any of its `activePaths`) prefixes
  // the current path. `activePaths` lets a section own routes that don't share
  // its href prefix (e.g. Campaigns owns the singular campaign-detail route).
  const allItems = [...navItems, ...footerItems];
  const activeItem = allItems.find((item) => {
    const paths = item.activePaths ?? (item.href ? [item.href] : []);
    return paths.some((p) => (p === '/' ? pathname === '/' : pathname.startsWith(p)));
  });
  const activeTab = activeItem?.id ?? '';
  const isNewCampaign = pathname.startsWith('/new-campaign');
  const isStoreDiscounts = pathname.startsWith('/integrations/shopify/discounts');
  const isFullContent = isCampaignDetailRoute(pathname) || isNewCampaign;
  const isHome = pathname === '/';
  const breadcrumbLabel = isNewCampaign
    ? 'Create campaign'
    : isCampaignDetailRoute(pathname)
      ? 'Campaign'
      : (activeItem?.label ?? 'Home');

  // Header share icon: only on a campaign-detail page and only once the share
  // stores are seeded (canShare). Copies the current screen's authenticated
  // brand-app URL — the tab drives both the subtitle and the copied link.
  const showShare = isCampaignDetailRoute(pathname) && canShare;
  const shareScreenName = shareSearch.tab ? titleCaseSlug(shareSearch.tab) : undefined;
  const getShareUrl = () => makeUrl({ tab: shareSearch.tab, sub: shareSearch.sub });

  const sidebarProps: AppSidebarProps = {
    navItems,
    footerItems,
    activeTab,
    onNavigateHome: () => navigate({ to: '/' }),
    userInitials,
    userName,
    userRole,
    onNavigateProfile: () => navigate({ to: '/settings' }),
    onSignOut: async () => { await signOut(); hardNavigate(SIGNED_OUT_PATH); },
    logoSrc: discovrLogo,
    logoMarkSrc: discovrMark,
    appName: 'Discovr',
    // Create Campaign CTA pinned above the footer (Figma).
    primaryAction: { label: 'Create Campaign', icon: Plus, onClick: () => navigate({ to: '/new-campaign' }) },
    // Search intentionally hidden until global search / command palette is built.
    // AppSidebar renders the search block only when onSearch is passed, so
    // re-enabling later is just restoring onSearch + searchPlaceholder here.
  };

  return (
    // Mounted once per authenticated session (this layout wraps all protected
    // routes). Picks up a pending public-share action attempted pre-sign-in,
    // whichever tab the visitor lands on after signing in.
    <>
      <PendingPublicActionResumeHost />
      <AppLayout
        sidebarProps={sidebarProps}
        contentVariant={isFullContent ? 'full' : 'centered'}
        collapseRail={isCampaignDetailRoute(pathname)}
        isLoading={loading || (!!user && !profile && !profileError)}
        headerLeft={
          <BrandBreadcrumb
            isHome={isHome}
            isStoreDiscounts={isStoreDiscounts}
            label={breadcrumbLabel}
            onHome={() => navigate({ to: '/' })}
          />
        }
        headerRight={
          isNewCampaign ? (
            // FE-only auto-save (localStorage); the label mirrors the draft writer's real state.
            <DraftStatusBadge />
          ) : showShare ? (
            // Same shared SharePopover the admin renders, in link-only mode:
            // brand gets just "Copy link" (no public toggle); the public block is
            // the only divergence. `input=null` — link-only uses screenName.
            <SharePopover input={null} getUrl={getShareUrl} screenName={shareScreenName} linkOnly label="Copy link" />
          ) : undefined
          /* Create Campaign top-bar CTA hidden for now. It used to render on the
             campaigns tab (Figma: white outlined Plus button, left of the bell).
             Restore by re-adding this branch before the final `: undefined`:
               : activeTab === 'campaigns' ? (
                   <button type="button" onClick={() => navigate({ to: '/new-campaign' })}
                     className="flex h-8 items-center gap-1.5 rounded-lg border border-border bg-card px-3.5 text-body-sm-medium text-foreground transition-colors hover:bg-muted">
                     <Plus size={16} /> Create Campaign
                   </button>
                 )                                                                */
        }
      >
        <Outlet />
      </AppLayout>
    </>
  );
}
